import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../store';

/**
 * Classes utilitaires (littéraux complets pour le scan Tailwind).
 * Le plein écran mobile repose surtout sur [data-sheet-expanded] dans main.css.
 */
const EXPAND_SM =
  'max-sm:!inset-x-0 max-sm:!top-0 max-sm:!bottom-0 max-sm:!max-h-none max-sm:!h-[100dvh] max-sm:!rounded-none';
const EXPAND_MD =
  'max-md:!inset-x-0 max-md:!top-0 max-md:!bottom-0 max-md:!max-h-none max-md:!h-[100dvh] max-md:!rounded-none';

export function sheetExpandClass(expanded: boolean, breakpoint: 'sm' | 'md' = 'sm'): string {
  const base = 'transition-[max-height,height,border-radius] duration-300 ease-out';
  if (!expanded) return base;
  return `${base} ${breakpoint === 'md' ? EXPAND_MD : EXPAND_SM}`;
}

/** Poignée en haut du sheet : tap = agrandir / réduire (mobile only). */
export function MobileSheetHandle({ until = 'sm' }: { until?: 'sm' | 'md' } = {}) {
  const { t } = useTranslation('common');
  const expanded = useApp((s) => s.mobileSheetExpanded);
  const setExpanded = useApp((s) => s.setMobileSheetExpanded);
  const ref = useRef<HTMLButtonElement>(null);
  const hideFrom = until === 'md' ? 'md:hidden' : 'sm:hidden';

  // Pose data-sheet-expanded sur l’<aside> parent (CSS plein écran dans main.css).
  useEffect(() => {
    const aside = ref.current?.closest('aside');
    if (!aside) return;
    aside.setAttribute('data-sheet-until', until);
    aside.setAttribute('data-sheet-expanded', expanded ? '1' : '0');
    return () => {
      aside.setAttribute('data-sheet-expanded', '0');
      aside.removeAttribute('data-sheet-until');
    };
  }, [expanded, until]);

  return (
    <button
      ref={ref}
      type="button"
      data-mobile-sheet-handle=""
      className={`flex min-h-11 w-full shrink-0 touch-manipulation flex-col items-center justify-center gap-1 border-b border-[var(--line)]/50 bg-black/25 ${hideFrom}`}
      aria-expanded={expanded}
      aria-label={expanded ? t('sheetCollapseTitle') : t('sheetExpandTitle')}
      title={expanded ? t('sheetCollapseTitle') : t('sheetExpandTitle')}
      onClick={() => setExpanded(!expanded)}
    >
      <span
        className={`block h-1.5 rounded-full bg-[var(--accent)]/80 transition-all duration-300 ${
          expanded ? 'w-8 opacity-70' : 'w-12'
        }`}
      />
      <span className="text-[10px] font-medium leading-none tracking-wide text-[var(--muted)]">
        {expanded ? t('sheetCollapse') : t('sheetExpand')}
      </span>
    </button>
  );
}

export function useMobileSheetExpand(breakpoint: 'sm' | 'md' = 'sm') {
  const expanded = useApp((s) => s.mobileSheetExpanded);
  return {
    expanded,
    className: sheetExpandClass(expanded, breakpoint),
  };
}
